// utils/IconExportOverviewData.tsx
import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import type ExcelJS from 'exceljs';
import { downloadBlob } from '@/utils/exportImage';
import { colors } from '@/styles/base/tokens';
import { bandIconButtonSx } from '@/styles/base/band';

interface SummaryRow {
    customer_name: string;
    campaign_name: string;
    booking_name: string;
    channel_name: string;
    start_date: string;
    end_date: string;
    impressions: number;
    spend: number;
    cpm: number;
    [key: string]: unknown;
}

interface IconExportOverviewDataProps {
    disabled?: boolean;
    summaryData?: SummaryRow[];
}

/**
 * Iconographic export button for the Overview page data
 * Matches the design language of the IconExportButton
 */
export const IconExportOverviewData = ({ disabled = false, summaryData = [] }: IconExportOverviewDataProps) => {
    const formatCurrency = (sheet: ExcelJS.Worksheet, columnName: string): void => {
        if (!sheet.actualColumnCount) return;

        const values = sheet.getRow(1).values;
        const headerRow = Array.isArray(values)
            ? values.slice(1).map((val) => val?.toString().trim().toLowerCase())
            : [];
        const columnIndex = headerRow.findIndex((val) => val === columnName.toLowerCase());

        if (columnIndex >= 0) {
            sheet.getColumn(columnIndex + 1).numFmt = '"$"#,##0.00';
        }
    };

    const applyHeaderStyle = (sheet: ExcelJS.Worksheet): void => {
        const headerRow = sheet.getRow(1);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFF' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: colors.excelHeader } };
            cell.alignment = { horizontal: 'center' };
        });
    };

    const autoSizeColumns = (sheet: ExcelJS.Worksheet): void => {
        for (let i = 1; i <= sheet.columnCount; i++) {
            const column = sheet.getColumn(i);
            let maxLength = 0;
            column.eachCell({ includeEmpty: true }, (cell) => {
                const cellLength = cell.value ? cell.value.toString().length : 10;
                if (cellLength > maxLength) {
                    maxLength = cellLength;
                }
            });
            column.width = Math.min(maxLength + 2, 30);
        }
    };

    const formatNumber = (sheet: ExcelJS.Worksheet, columnName: string): void => {
        if (!sheet.actualColumnCount) return;

        const raw = sheet.getRow(1).values;
        const cells = Array.isArray(raw) ? raw.slice(1) : Object.values(raw);
        const headerRow = cells.map((val: ExcelJS.CellValue) => val?.toString().trim().toLowerCase());
        const columnIndex = headerRow.findIndex((val) => val === columnName.toLowerCase());

        if (columnIndex >= 0) {
            sheet.getColumn(columnIndex + 1).numFmt = '#,##0';
        }
    };

    const handleExport = async (): Promise<void> => {
        if (disabled || !summaryData.length) return;

        const excelModule = await import('exceljs');
        const ExcelJS = ('default' in excelModule ? excelModule.default : excelModule) as typeof import('exceljs');
        const workbook = new ExcelJS.Workbook();
        workbook.creator = 'Infinitum Dashboard';
        workbook.lastModifiedBy = 'Infinitum Analytics';
        workbook.created = new Date();
        workbook.modified = new Date();

        const sheet = workbook.addWorksheet('Campaign Overview');

        const exportData = summaryData.map(
            ({
                customer_name,
                campaign_name,
                booking_name,
                channel_name,
                start_date,
                end_date,
                impressions,
                spend,
                cpm,
            }) => ({
                Client: customer_name,
                Campaign: campaign_name,
                Booking: booking_name,
                Channel: channel_name,
                'Start Date': start_date,
                'End Date': end_date,
                Impressions: impressions,
                Spend: spend,
                CPM: cpm,
            })
        );

        sheet.addRow(Object.keys(exportData[0]));
        exportData.forEach((row) => sheet.addRow(Object.values(row)));

        formatCurrency(sheet, 'Spend');
        formatCurrency(sheet, 'CPM');
        formatNumber(sheet, 'Impressions');
        applyHeaderStyle(sheet);
        autoSizeColumns(sheet);

        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
        const filename = `Infinitum_Overview_${timestamp}.xlsx`;

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        downloadBlob(filename, blob);
    };

    React.useEffect(() => {
        window.exportOverviewData = handleExport;
        return () => {
            delete window.exportOverviewData;
        };
    }, [summaryData]);

    return (
        <Tooltip title="Export campaign overview data">
            <span>
                {' '}
                {/* Wrapper to handle disabled tooltip behavior */}
                <IconButton onClick={handleExport} disabled={disabled} sx={bandIconButtonSx}>
                    <FileDownloadIcon fontSize="small" />
                </IconButton>
            </span>
        </Tooltip>
    );
};
